import type { ReactNode } from 'react';
import { useState, useCallback } from 'react';
import type { CalendarContainerProps } from 'react-datepicker';
import DatePicker, { CalendarContainer } from 'react-datepicker';
import { noop } from 'shared/lib/functions';
import classNames from 'classnames';
import { Tooltip } from 'shared/ui/Tooltip';
import type { PickerValue } from 'shared/ui/DatePicker/date-ranges';
import { CustomHeader } from 'shared/ui/DatePicker/CustomHeader';

import { flatHolidays, getMinDate } from './lib';
import { monthsNameToNumber, msInOneDay, numberToMonthName } from './constants';
import type { FormattedHolidays } from './model';

const isDateAvailable = (date: Date) => {
  return getMinDate().getTime() - msInOneDay < date.getTime();
};

const isDateSelected = (holidays: FormattedHolidays | null, date: Date) => {
  if (!holidays) {
    return null;
  }

  const year = date.getFullYear();
  const month = numberToMonthName[date.getMonth() + 1];
  const day = date.getDate();

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return holidays[year] && holidays[year][month] && holidays[year][month][day]?.isChecked;
};

const getExcludedDates = (holidays: FormattedHolidays | null) => {
  if (!holidays) {
    return [];
  }

  const excludedHolidays = flatHolidays(holidays)
    .filter((item) => item.isChecked)
    .map(({ day, month, year }) => {
      return new Date(`${monthsNameToNumber[month]}-${day}-${year}`);
    });

  return excludedHolidays;
};

type DatePickerHolidayProps = {
  placeholder?: string;
  className?: string;
  onChange?: (value: Date) => void;
  value?: Date;
  children: JSX.Element | string;
  openToDate?: Date;
  holidays: FormattedHolidays | null;
};

export function DatePickerHoliday({
  placeholder,
  className,
  onChange = noop,
  value,
  children,
  openToDate,
  holidays,
}: DatePickerHolidayProps) {
  const [date, setDate] = useState<PickerValue>(value || null);
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const onSetDateRange = useCallback(
    (value: Date) => {
      onChange(value);
      setDate(value);
    },
    [onChange]
  );

  const customCalendarContainer = ({ className, children }: CalendarContainerProps) => {
    return (
      isOpen && (
        <CalendarContainer className={classNames(className, '!pl-8 !pr-5')}>
          <div className="flex">
            <div className="relative">{children}</div>
          </div>
        </CalendarContainer>
      )
    );
  };

  const getDayClass = (date: Date): string | null => {
    const isSelected = isDateSelected(holidays, date);
    const isAvailable = isDateAvailable(date);

    return (
      classNames('rounded-sm', {
        'bg-slate-25 hover:!bg-slate-25': isSelected && !isAvailable,
        'bg-deep-blue-50 hover:!bg-deep-blue-50 !text-slate-900 cursor-not-allowed': isSelected && isAvailable,
      }) || null
    );
  };

  const renderDayContents = (dayOfMonth: number, date?: Date | undefined): ReactNode => {
    if (!date) {
      return null;
    }

    const isExcludedDay = getExcludedDates(holidays).find((item) => item.getTime() === date.getTime());
    const isAvailable = isDateAvailable(date);

    const tooltipText = (
      <div className="text-start">
        {isExcludedDay
          ? 'This date is already included.'
          : 'This date cannot be added because it is two weeks or less before the event. Please contact your partnerships manager.'}
      </div>
    );

    if (isAvailable && !isExcludedDay) {
      return dayOfMonth;
    }

    return (
      <Tooltip
        placement="top"
        widthClass="max-w-[291px]"
        content={tooltipText}
        contentClass="!pl-3 !pr-2"
      >
        <div className="w-8">{dayOfMonth}</div>
      </Tooltip>
    );
  };

  return (
    <DatePicker
      renderCustomHeader={CustomHeader}
      formatWeekDay={(nameOfDay) => nameOfDay.toString().substring(0, 3)}
      startDate={date}
      onChange={onSetDateRange}
      placeholderText={placeholder}
      calendarContainer={customCalendarContainer}
      onCalendarOpen={() => setIsOpen(true)}
      onCalendarClose={() => setIsOpen(false)}
      customInput={
        <div>
          <div onClick={() => setIsOpen((isOpen) => !isOpen)}>{children}</div>
        </div>
      }
      dayClassName={getDayClass}
      className={className}
      minDate={getMinDate()}
      disabledKeyboardNavigation
      popperPlacement="top"
      calendarClassName="single-datepicker"
      calendarStartDay={1}
      renderDayContents={renderDayContents}
      openToDate={openToDate}
      excludeDates={getExcludedDates(holidays)}
      popperModifiers={[
        {
          name: 'flip',
          enabled: false,
        },
      ]}
    />
  );
}
